import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientAPI, visitationAPI, treatmentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

function PatientDetails() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [visitations, setVisitations] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [sales, setSales] = useState([]);
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLobbyDialog, setShowLobbyDialog] = useState(false);
  const [lobbyVisitType, setLobbyVisitType] = useState('Visitation');
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressForm, setAddressForm] = useState({ street: '', city: '', state: '', zipCode: '' });
  const [savingAddress, setSavingAddress] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchPatientDetails();
    fetchVisitations();
    fetchTreatments();
    fetchSales();
  }, [patientId]);

  const fetchPatientDetails = async () => {
    try {
      const response = await patientAPI.getById(patientId);
      setPatient(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch patient details');
    }
  };

  const fetchVisitations = async () => {
    try {
      setLoading(true);
      const response = await visitationAPI.getByPatientId(patientId);
      setVisitations(response.data.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch visitations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTreatments = async () => {
    try {
      const response = await treatmentAPI.getByPatientId(patientId);
      setTreatments(response.data.data);
    } catch (err) {
      console.error('Failed to fetch treatments:', err);
    }
  };

  const fetchSales = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/sales/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data);
    } catch (err) {
      console.error('Failed to fetch sales:', err);
    }
  };

  // const handleDeleteVisitation = async (visitationId) => {
  //   if (window.confirm('Are you sure you want to delete this visitation?')) {
  //     try {
  //       await visitationAPI.delete(visitationId);
  //       fetchVisitations();
  //     } catch (err) {
  //       alert(err.response?.data?.error || 'Failed to delete visitation');
  //     }
  //   }
  // };

  // const handleDeleteTreatment = async (treatmentId) => {
  //   if (window.confirm('Are you sure you want to delete this treatment record?')) {
  //     try {
  //       await treatmentAPI.delete(treatmentId);
  //       fetchTreatments();
  //     } catch (err) {
  //       alert(err.response?.data?.error || 'Failed to delete treatment');
  //     }
  //   }
  // };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const openEditDialog = () => {
    setEditForm({
      firstName: patient.firstName || '',
      lastName: patient.lastName || '',
      dateOfBirth: patient.dateOfBirth || '',
      gender: patient.gender || '',
      phone: patient.phone || '',
      email: patient.email || '',
      medicalHistory: patient.medicalHistory || '',
      allergies: patient.allergies || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.firstName.trim()) return;
    setSavingEdit(true);
    try {
      await patientAPI.update(patientId, editForm);
      setPatient(prev => ({ ...prev, ...editForm }));
      setShowEditDialog(false);
      setSuccess('Patient details updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update patient details');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSavingEdit(false);
    }
  };

  const openAddressDialog = () => {
    setAddressForm({
      street: patient.street || '',
      city: patient.city || '',
      state: patient.state || '',
      zipCode: patient.zipCode || ''
    });
    setShowAddressDialog(true);
  };

  const handleSaveAddress = async () => {
    setSavingAddress(true);
    try {
      await patientAPI.update(patientId, addressForm);
      setPatient(prev => ({ ...prev, ...addressForm }));
      setShowAddressDialog(false);
      setSuccess('Address updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update address');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleAddToLobby = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/lobby`,
        {
          patientId: patient.id,
          visitType: lobbyVisitType,
          markedBy: user?.name || 'Reception'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(`Patient added to waiting area for ${lobbyVisitType}`);
      setShowLobbyDialog(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to add to lobby:', err);
      setError(err.response?.data?.error || 'Failed to add patient to waiting area');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handlePrintBill = (sale) => {
    const ITEMS_PER_PAGE = 12;
    const CSS = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      @page { size: A5; margin: 8mm; }
      body { font-family: 'Arial', sans-serif; font-size: 9pt; }
      .bill { border: 1px solid #000; padding: 5mm; page-break-after: always; break-after: page; }
      .bill.last-page { page-break-after: auto; break-after: auto; }
      .header { border-bottom: 1px dashed #000; padding-bottom: 3mm; margin-bottom: 3mm; }
      .header-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2mm; }
      .header-logo { width: 22mm; height: auto; object-fit: contain; }
      .header-center { flex: 1; text-align: center; padding: 0 2mm; }
      .header-center h1 { font-size: 12pt; margin-bottom: 1mm; font-weight: bold; line-height: 1.2; }
      .header-center h2 { font-size: 8pt; margin-bottom: 1mm; font-weight: 600; line-height: 1.2; }
      .header-center .address { font-size: 7pt; line-height: 1.3; margin: 0; }
      .bill-details { text-align: center; margin-top: 2mm; }
      .bill-details p { font-size: 8pt; margin: 0.5mm 0; line-height: 1.2; }
      .page-num { text-align: right; font-size: 7pt; color: #666; margin-bottom: 1mm; }
      .section { margin: 2mm 0; }
      .section-title { font-weight: bold; font-size: 9pt; border-bottom: 1px solid #000; margin-bottom: 2mm; padding-bottom: 1mm; }
      .row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 8pt; line-height: 1.3; }
      .info-row { justify-content: flex-start; gap: 2mm; }
      .info-row span:first-child { font-weight: bold; }
      .patient-info-container { display: flex; justify-content: space-between; align-items: flex-start; }
      .patient-info-right { text-align: left; max-width: 50%; font-size: 8pt; line-height: 1.3; }
      .items-table { width: 100%; margin: 2mm 0; border-collapse: collapse; }
      .items-table th { text-align: left; border-bottom: 1px solid #000; padding: 1.5mm 0; font-size: 8pt; font-weight: bold; }
      .items-table td { padding: 1mm 0; font-size: 8pt; line-height: 1.2; }
      .item-name { width: 35%; }
      .item-qty { width: 10%; text-align: center; }
      .item-price { width: 15%; text-align: right; }
      .item-gst { width: 10%; text-align: right; font-size: 7pt; }
      .item-total { width: 15%; text-align: right; }
      .item-amt { width: 15%; text-align: right; }
      .totals { border-top: 1px dashed #000; padding-top: 2mm; margin-top: 2mm; }
      .totals .row { font-size: 9pt; margin: 1mm 0; }
      .totals .final-total { font-size: 11pt; font-weight: bold; border-top: 1px solid #000; padding-top: 1.5mm; margin-top: 1.5mm; }
      .footer { text-align: center; border-top: 1px dashed #000; padding-top: 2mm; margin-top: 3mm; font-size: 7pt; line-height: 1.3; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
      @media screen { body { max-width: 148mm; margin: 0 auto; background: #f0f0f0; padding: 10mm; } }`;

    const headerHTML = `
      <div class="header">
        <div class="header-top">
          <img src="/logo.jpg" alt="Logo" class="header-logo" />
          <div class="header-center">
            <h1>GLOW with SuperDoc</h1>
            <h2>MSG Hotels &amp; Resorts</h2>
            <p class="address">Opp. MSG International School</p>
            <p class="address">Nezia Khera, Sirsa</p>
            <p class="address">Haryana, India</p>
            <p class="address">Pin Code 125055</p>
            <p class="address">GST NO: 06AAJCA4867D4ZL</p>
          </div>
          <img src="/logo2.png" alt="Logo" class="header-logo" />
        </div>
        <div class="bill-details">
          <p><strong>Medicine Sales Receipt</strong></p>
          <p>Bill No: ${sale.billNumber}</p>
          <p>Date: ${new Date(sale.saleDate).toLocaleDateString()} ${new Date(sale.createdAt || sale.saleDate || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</p>
        </div>
      </div>`;

    const patientAddress = patient ? [patient.street, patient.city, patient.state, patient.zipCode].filter(Boolean).join(', ') : '';

    const patientHTML = `
      <div class="section">
        <div class="section-title">Customer Details</div>
        <div class="patient-info-container">
          <div>
            <div class="row info-row"><span>Name:</span><span>${sale.customerName}</span></div>
            ${sale.customerPhone ? `<div class="row info-row"><span>Phone:</span><span>${sale.customerPhone}</span></div>` : ''}
          </div>
          ${patientAddress ? `<div class="patient-info-right"><strong>Address:</strong> ${patientAddress}</div>` : ''}
        </div>
      </div>`;

    const itemsHeaderHTML = `<tr>
      <th class="item-name">Medicine</th>
      <th class="item-qty">Qty</th>
      <th class="item-price">Rate</th>
      <th class="item-total">Amount</th>
      <th class="item-gst">GST<br/>(5%)</th>
      <th class="item-amt">Total</th>
    </tr>`;

    let itemsList = [];
    if (sale && sale.items) {
      if (Array.isArray(sale.items)) {
        itemsList = sale.items;
      } else if (typeof sale.items === 'string') {
        try {
          const parsed = JSON.parse(sale.items);
          if (Array.isArray(parsed)) {
            itemsList = parsed;
          } else if (typeof parsed === 'string') {
            const doubleParsed = JSON.parse(parsed);
            if (Array.isArray(doubleParsed)) {
              itemsList = doubleParsed;
            }
          }
        } catch (e) {
          console.error('Error parsing items:', e);
        }
      }
    }

    const itemRows = itemsList.map(item => {
      const itemTotal = Math.round(parseFloat(item.total));
      const amtBeforeGST = Math.round(itemTotal / 1.05);
      const gstAmt = Math.round(itemTotal - amtBeforeGST);
      const priceBeforeGST = Math.round(parseFloat(item.price) / 1.05);
      return `<tr>
        <td class="item-name">${item.medicineName}</td>
        <td class="item-qty">${item.quantity}</td>
        <td class="item-price">₹${priceBeforeGST.toLocaleString('en-IN')}</td>
        <td class="item-total">₹${amtBeforeGST.toLocaleString('en-IN')}</td>
        <td class="item-gst">₹${gstAmt.toLocaleString('en-IN')}</td>
        <td class="item-amt">₹${itemTotal.toLocaleString('en-IN')}</td>
      </tr>`;
    });

    const taxable = Math.round(parseFloat(sale.totalAmount) - parseFloat(sale.discount || 0));
    const subtotalBeforeGST = Math.round(taxable / 1.05);
    const totalGST = taxable - subtotalBeforeGST;
    const halfGST = Math.round(totalGST / 2);

    const totalsHTML = `
      <div class="totals">
        <div class="row"><span>Subtotal (Before GST):</span><span>₹${subtotalBeforeGST.toLocaleString('en-IN')}</span></div>
        ${sale.discount > 0 ? `<div class="row"><span>Discount (${sale.discountPercentage}%):</span><span>- ₹${Math.round(parseFloat(sale.discount)).toLocaleString('en-IN')}</span></div>` : ''}
        <div class="row"><span>CGST (2.5%):</span><span>₹${halfGST.toLocaleString('en-IN')}</span></div>
        <div class="row"><span>SGST (2.5%):</span><span>₹${(totalGST - halfGST).toLocaleString('en-IN')}</span></div>
        <div class="row"><span>Total GST (5%):</span><span>₹${totalGST.toLocaleString('en-IN')}</span></div>
        <div class="row final-total"><span>Total Amount:</span><span>₹${Math.round(parseFloat(sale.totalAmount)).toLocaleString('en-IN')}</span></div>
        <div class="row"><span>Paid:</span><span>₹${Math.round(parseFloat(sale.paidAmount)).toLocaleString('en-IN')}</span></div>
        ${sale.changeAmount > 0 ? `<div class="row"><span>Change:</span><span>₹${Math.round(parseFloat(sale.changeAmount)).toLocaleString('en-IN')}</span></div>` : ''}
      </div>`;

    const servedByHTML = `
      <div class="section">
        <div class="row"><span>Payment Method:</span><span>${sale.paymentMethod}</span></div>
        <div class="row"><span>Served By:</span><span>${sale.soldBy}</span></div>
      </div>`;

    // Split items into pages
    const chunks = [];
    for (let i = 0; i < itemRows.length; i += ITEMS_PER_PAGE) {
      chunks.push(itemRows.slice(i, i + ITEMS_PER_PAGE));
    }
    if (chunks.length === 0) chunks.push([]);
    const totalPages = chunks.length;

    const pagesHTML = chunks.map((chunk, pageIdx) => {
      const isLast = pageIdx === totalPages - 1;
      return `
        <div class="bill${isLast ? ' last-page' : ''}">
          ${headerHTML}
          ${patientHTML}
          ${totalPages > 1 ? `<div class="page-num">Page ${pageIdx + 1} of ${totalPages}</div>` : ''}
          <div class="section">
            <div class="section-title">Items</div>
            <table class="items-table">
              <thead>${itemsHeaderHTML}</thead>
              <tbody>${chunk.join('')}</tbody>
            </table>
          </div>
          ${isLast ? totalsHTML + servedByHTML : ''}
          <div class="footer">
            ${isLast
          ? '<p>Thank you for your purchase!</p><p>Please keep this bill for your records</p>'
          : `<p style="font-style:italic">Continued on next page (${pageIdx + 1}/${totalPages})</p>`}
          </div>
        </div>`;
    }).join('');

    const printContent = `<!DOCTYPE html>
      <html>
      <head>
        <title>Bill - ${sale.billNumber}</title>
        <style>${CSS}</style>
      </head>
      <body>
        ${pagesHTML}
        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">Print Bill</button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; margin-left: 10px;">Close</button>
        </div>
      </body>
      </html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handlePrintTodayRecord = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter today's visitations
    const todayVisitations = visitations.filter(v => {
      const visitDate = new Date(v.visitDate);
      visitDate.setHours(0, 0, 0, 0);
      return visitDate.getTime() === today.getTime();
    });

    // Get today's treatment sessions
    const todaySessions = [];
    for (const treatment of treatments) {
      if (treatment.sessions && treatment.sessions.length > 0) {
        const sessionsToday = treatment.sessions.filter(s => {
          const sessionDate = new Date(s.sessionDate);
          sessionDate.setHours(0, 0, 0, 0);
          return sessionDate.getTime() === today.getTime();
        });
        if (sessionsToday.length > 0) {
          todaySessions.push({
            treatment: treatment,
            sessions: sessionsToday
          });
        }
      }
    }

    if (todayVisitations.length === 0 && todaySessions.length === 0) {
      alert('No records found for today');
      return;
    }

    // Consolidate medicines from all sources
    const consolidatedMedicines = [];
    const medicineMap = new Map();

    // Add medicines from visitations
    todayVisitations.forEach(visitation => {
      if (visitation.prescription && visitation.prescription.length > 0) {
        visitation.prescription.forEach(med => {
          const key = med.medicineName;
          if (!medicineMap.has(key)) {
            medicineMap.set(key, { ...med });
          }
        });
      }
    });

    // Add medicines from treatment sessions
    todaySessions.forEach(({ sessions }) => {
      sessions.forEach(session => {
        if (session.prescribedMedicines && session.prescribedMedicines.length > 0) {
          session.prescribedMedicines.forEach(med => {
            const key = med.medicineName;
            if (!medicineMap.has(key)) {
              medicineMap.set(key, { ...med });
            }
          });
        }
      });
    });

    consolidatedMedicines.push(...medicineMap.values());

    // Consolidate treatments
    const consolidatedTreatments = [];
    todayVisitations.forEach(visitation => {
      if (visitation.treatment && visitation.treatment.length > 0) {
        visitation.treatment.forEach(treat => {
          consolidatedTreatments.push({
            ...treat,
            source: 'visitation'
          });
        });
      }
    });

    todaySessions.forEach(({ treatment, sessions }) => {
      sessions.forEach(session => {
        consolidatedTreatments.push({
          treatmentName: treatment.treatmentType,
          sessionNumber: session.sessionNumber,
          source: 'session',
          performedBy: session.performedBy,
          notes: session.notes
        });
      });
    });

    // Get chief complaint and diagnosis from visitation or first session
    let chiefComplaint = '';
    let diagnosis = '';

    if (todayVisitations.length > 0) {
      chiefComplaint = todayVisitations[0].chiefComplaint || '';
      diagnosis = todayVisitations[0].diagnosis || '';
    }

    const printWindow = window.open('', '_blank');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Today's Record - ${patient.firstName} ${patient.lastName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Roboto', Arial, sans-serif;
            padding: 20px;
            margin: 0;
          }
          .prescription-print {
            margin-top: 29%;
            margin-left: 0;
            margin-right: 0;
            width: 100%;
            font-size: 14px;
            box-sizing: border-box;
          }
          .prescription-print h3 {
            font-size: 15px;
            margin-top: 10px;
            margin-bottom: 4px;
            font-weight: 600;
            color: #2196F3;
          }
          .prescription-print p {
            margin-bottom: 4px;
            line-height: 1.4;
          }
          .prescription-print table {
            margin-top: 4px;
            margin-bottom: 25px;
            margin-right: 0;
            width: 100%;
            font-size: 14px;
            border: none;
            border-collapse: collapse;
          }
          .prescription-print tr {
            border: none;
          }
          .prescription-print table td {
            padding: 4px 6px;
          }
          .prescription-medicines {
            margin: 15px 0;
          }
          .prescription-footer {
            margin-top: 30px;
            text-align: right;
          }
          .prescription-footer p {
            margin: 2px 0;
          }
          .no-print {
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: white;
            padding: 15px;
            border: 2px solid #333;
            border-radius: 8px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          }
          @media print {
            body { padding: 0 10mm; margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="prescription-print">
          <table>
            <tr>
              <td><div><strong>Patient:</strong> ${patient.firstName} ${patient.lastName}</div></td>
              <td style="text-align: right;"><div><strong>Date:</strong> ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div></td>
            </tr>
            <tr>
              <td>
                <div style="margin-top: 8px;"><strong>Age / Gender:</strong> ${calculateAge(patient.dateOfBirth)} years / ${patient.gender}</div>
              </td>
            </tr>
          </table>

          ${chiefComplaint ? `
          <div>
            <h3><strong>Chief Complaint:</strong></h3>
            <p>${chiefComplaint}</p>
          </div>
          ` : ''}

          ${diagnosis ? `
          <div>
            <h3><strong>Diagnosis:</strong></h3>
            <p>${diagnosis}</p>
          </div>
          ` : ''}

          ${consolidatedTreatments.length > 0 ? `
          <div class="prescription-medicines">
            <h3><strong>Treatment Plan</strong></h3>
            ${consolidatedTreatments.map((treat, index) => `
              <div>
                <p style="margin-bottom: 0.25rem; line-height: 1.6;">
                  ${index + 1}. ${treat.treatmentName}${treat.sessionNumber ? ` - Session ${treat.sessionNumber}` : ''}${treat.frequency ? ` ${treat.frequency}` : ''}${treat.sessions ? ` (${treat.sessions} sessions planned)` : ''}${treat.description ? ` - ${treat.description}` : ''}
                </p>
                ${treat.performedBy ? `<p style="margin-left: 1.5rem; margin-bottom: 0.5rem;"><em>Performed by: ${treat.performedBy}</em></p>` : ''}
                ${treat.notes ? `<p style="margin-left: 1.5rem; margin-bottom: 0.5rem;"><em>Notes: ${treat.notes}</em></p>` : ''}
              </div>
            `).join('')}
          </div>
          ` : ''}

          ${consolidatedMedicines.length > 0 ? `
          <div class="prescription-medicines">
            <h3><strong>Prescribed Medicines</strong></h3>
            ${consolidatedMedicines.map((med, index) => `
              <p style="margin-bottom: 0.5rem; line-height: 1.6;">
                ${index + 1}. ${med.medicineName} ${med.dosage} ${med.frequency} ${med.duration || ''}${med.instructions ? ` - ${med.instructions}` : ''}
              </p>
            `).join('')}
          </div>
          ` : ''}

          ${todayVisitations.length > 0 && todayVisitations[0].notes ? `
          <div style="margin-top: 2rem;">
            <h3><strong>Instructions:</strong></h3>
            <p>${todayVisitations[0].notes}</p>
          </div>
          ` : ''}

          ${todayVisitations.length > 0 && todayVisitations[0].followUpDate ? `
          <div style="margin-top: 2rem;">
            <h3><strong>Follow-up</strong></h3>
            <p><strong>Next Appointment:</strong> ${new Date(todayVisitations[0].followUpDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          ` : ''}

          <div class="prescription-footer">
            <p>_________________________</p>
            ${todayVisitations.length > 0 ? `<p>Dr. ${todayVisitations[0].doctorName}</p>` : ''}
            <p>Signature & Date</p>
          </div>
        </div>

        <div class="no-print">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; margin-right: 10px; background: #2196F3; color: white; border: none; border-radius: 4px;">Print</button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #666; color: white; border: none; border-radius: 4px;">Close</button>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  if (loading && !patient) {
    return <div className="loading">Loading patient details...</div>;
  }

  if (!patient) {
    return <div className="error-message">❌ Patient not found</div>;
  }

  return (
    <div>
      <div className="card">
        <h2 style={{ fontSize: '1.3rem' }}>👤 {patient.firstName} {patient.lastName} {patient.patientId && <span className="badge badge-primary" style={{ fontSize: '0.7rem', marginLeft: '0.5rem' }}>{patient.patientId}</span>}</h2>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="btn-group">
          <button
            className="btn btn-warning"
            onClick={() => setShowLobbyDialog(true)}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
          >
            🪑 Add to Waiting Area
          </button>
          {user?.role === 'Doctor' && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/visitation/new/${patientId}`)}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
              >
                + New Visitation
              </button>
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/treatment/new/${patientId}`)}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
              >
                + New Treatment
              </button>
              <button
                className="btn btn-success"
                onClick={() => navigate(`/patient/${patientId}/sell-medicines`)}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
              >
                💊 Sell Medicines
              </button>
            </>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/patients')}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
          >
            ← Back to Patient List
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="card" style={{ padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', borderBottom: '2px solid #e0e0e0' }}>
          <button
            onClick={() => setActiveTab('personal')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'personal' ? '3px solid var(--primary-color)' : '3px solid transparent',
              color: activeTab === 'personal' ? 'var(--primary-color)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'personal' ? '600' : '400',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s'
            }}
          >
            👤 Personal Info
          </button>
          <button
            onClick={() => setActiveTab('visitations')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'visitations' ? '3px solid var(--primary-color)' : '3px solid transparent',
              color: activeTab === 'visitations' ? 'var(--primary-color)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'visitations' ? '600' : '400',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s'
            }}
          >
            📋 Visitations ({visitations.length})
          </button>
          <button
            onClick={() => setActiveTab('treatments')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'treatments' ? '3px solid var(--primary-color)' : '3px solid transparent',
              color: activeTab === 'treatments' ? 'var(--primary-color)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'treatments' ? '600' : '400',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s'
            }}
          >
            💉 Treatments ({treatments.length})
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'sales' ? '3px solid var(--primary-color)' : '3px solid transparent',
              color: activeTab === 'sales' ? 'var(--primary-color)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'sales' ? '600' : '400',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s'
            }}
          >
            💊 Sales ({sales.length})
          </button>
        </div>
      </div>

      {/* Personal Information Tab */}
      {activeTab === 'personal' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ margin: 0 }}>👤 Personal Information</h2>
            {(user?.role === 'Doctor' || user?.role === 'Reception') && (
              <button
                className="btn btn-secondary"
                onClick={openEditDialog}
                style={{ padding: '0.4rem 0.9rem', fontSize: '0.85rem' }}
              >
                ✏️ Edit Details
              </button>
            )}
          </div>

          <div className="info-box">
            <h3 style={{ marginBottom: '0.75rem', color: 'var(--primary-color)', fontSize: '1rem' }}>Personal Details</h3>
            {patient.patientId && (
              <p><strong>Patient ID:</strong> <span className="badge badge-primary">{patient.patientId}</span></p>
            )}
            <p><strong>Name:</strong> {patient.firstName} {patient.lastName || ''}</p>
            <p><strong>Age:</strong> {calculateAge(patient.dateOfBirth)} years</p>
            <p><strong>Gender:</strong> {patient.gender}</p>
            <p><strong>Date of Birth:</strong> {new Date(patient.dateOfBirth).toLocaleDateString()}</p>
            <p><strong>Phone:</strong> {patient.phone}</p>
            <p><strong>Email:</strong> {patient.email || '-'}</p>
          </div>

          <div className="info-box" style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ color: 'var(--primary-color)', fontSize: '1rem', margin: 0 }}>📍 Address</h3>
              <button
                className="btn btn-secondary"
                onClick={openAddressDialog}
                style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}
              >
                {(patient.street || patient.city || patient.state || patient.zipCode) ? '✏️ Edit Address' : '+ Add Address'}
              </button>
            </div>
            {(patient.street || patient.city || patient.state || patient.zipCode) ? (
              <>
                {patient.street && <p>{patient.street}</p>}
                <p>{[patient.city, patient.state, patient.zipCode].filter(Boolean).join(', ')}</p>
              </>
            ) : (
              <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No address on record</p>
            )}
          </div>

          {(patient.medicalHistory || patient.allergies) && (
            <div className="info-box" style={{ marginTop: '1rem' }}>
              <h3 style={{ marginBottom: '0.75rem', color: 'var(--primary-color)', fontSize: '1rem' }}>🏥 Medical Information</h3>
              {patient.medicalHistory && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <p><strong>Medical History</strong></p>
                  <p style={{ marginTop: '0.25rem', whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>{patient.medicalHistory}</p>
                </div>
              )}
              {patient.allergies && (
                <div>
                  <p><strong>⚠️ Allergies</strong></p>
                  <p style={{ marginTop: '0.25rem', color: 'var(--error-color)', fontWeight: '600', whiteSpace: 'pre-wrap' }}>{patient.allergies}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Visitations Tab */}
      {activeTab === 'visitations' && (
        <div className="card">
          <h2>📋 Visitation History</h2>

          {error && <div className="error-message">❌ {error}</div>}

          {loading ? (
            <div className="loading">Loading visitations...</div>
          ) : visitations.length === 0 ? (
            <div>No visitations recorded yet.</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Visit Date</th>
                    <th>Doctor</th>
                    <th>Follow Up Date</th>
                    {/* <th>Chief Complaint</th>
                  <th>Diagnosis</th> */}
                    {/* <th>Prescriptions</th>
                  <th>Treatments</th> */}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitations.map(visitation => (
                    <tr key={visitation.id}>
                      <td>{formatDate(visitation.visitDate)}</td>
                      <td>{visitation.doctorName}</td>
                      <td>{visitation.followUpDate ? visitation.followUpDate : '-'}</td>
                      {/* <td>{visitation.chiefComplaint || '-'}</td>
                    <td>{visitation.diagnosis || '-'}</td> */}
                      {/* <td>
                      {visitation.prescription && visitation.prescription.length > 0
                        ? <span className="badge badge-info">{visitation.prescription.length} items</span>
                        : '-'}
                    </td>
                    <td>
                      {visitation.treatment && visitation.treatment.length > 0
                        ? <span className="badge badge-success">{visitation.treatment.length} items</span>
                        : '-'}
                    </td> */}
                      <td>
                        <button
                          className="btn btn-primary"
                          onClick={() => navigate(`/visitation/${visitation.id}`)}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          View
                        </button>
                        {/* {user?.role === 'Doctor' && (
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteVisitation(visitation.id)}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          Delete
                        </button>
                      )} */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Treatments Tab */}
      {activeTab === 'treatments' && (
        <div className="card">
          <h2>💉 Treatment History</h2>

          {loading ? (
            <div className="loading">Loading treatments...</div>
          ) : treatments.length === 0 ? (
            <div>No treatment records yet.</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Treatment Date</th>
                    <th>Treatment Type</th>
                    <th>Performed By</th>
                    <th>Duration</th>
                    <th>Cost</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {treatments.map(treatment => (
                    <tr key={treatment.id}>
                      <td>{formatDate(treatment.treatmentDate)}</td>
                      <td>{treatment.treatmentType}</td>
                      <td>{treatment.performedBy}</td>
                      <td>{treatment.duration || '-'}</td>
                      <td>{treatment.cost ? `$${parseFloat(treatment.cost).toFixed(2)}` : '-'}</td>
                      <td>
                        <span
                          className={`badge ${treatment.status === 'Completed'
                              ? 'badge-success'
                              : treatment.status === 'In Progress'
                                ? 'badge-info'
                                : 'badge-warning'
                            }`}
                        >
                          {treatment.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-primary"
                          onClick={() => navigate(`/treatment/${treatment.id}`)}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          View
                        </button>
                        {/* {user?.role === 'Doctor' && (
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDeleteTreatment(treatment.id)}
                            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                          >
                            Delete
                          </button>
                        )} */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Sales Tab */}
      {activeTab === 'sales' && (
        <div className="card">
          <h2>💊 Sales History</h2>

          {loading ? (
            <div className="loading">Loading sales...</div>
          ) : sales.length === 0 ? (
            <div>No sales recorded yet.</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Bill Number</th>
                    <th>Sale Date</th>
                    <th>Items</th>
                    <th>Total Amount</th>
                    <th>Payment Method</th>
                    <th>Payment Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map(sale => (
                    <tr key={sale.id}>
                      <td>
                        <span className="badge badge-info">{sale.billNumber}</span>
                      </td>
                      <td>{formatDate(sale.saleDate)}</td>
                      <td>
                        <span className="badge badge-primary">
                          {sale.items.length} item{sale.items.length !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td>
                        <strong>₹{parseFloat(sale.totalAmount).toFixed(2)}</strong>
                      </td>
                      <td>{sale.paymentMethod}</td>
                      <td>
                        <span
                          className={`badge ${sale.paymentStatus === 'Paid'
                              ? 'badge-success'
                              : sale.paymentStatus === 'Pending'
                                ? 'badge-warning'
                                : 'badge-info'
                            }`}
                        >
                          {sale.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-primary"
                          onClick={() => handlePrintBill(sale)}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                        >
                          🖨️ Print Bill
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit Personal Details Dialog */}
      {showEditDialog && (
        <div className="modal-overlay" onClick={() => setShowEditDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '540px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>✏️ Edit Patient Details</h3>

            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>

              {/* Name */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>First Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="First name"
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Last name (optional)"
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* DOB + Gender */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Date of Birth *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={editForm.dateOfBirth}
                    onChange={(e) => setEditForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Gender *</label>
                  <select
                    className="form-control"
                    value={editForm.gender}
                    onChange={(e) => setEditForm(prev => ({ ...prev, gender: e.target.value }))}
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Phone + Email */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Phone *</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={editForm.phone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number"
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email (optional)"
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Medical History */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Medical History</label>
                <textarea
                  className="form-control"
                  value={editForm.medicalHistory}
                  onChange={(e) => setEditForm(prev => ({ ...prev, medicalHistory: e.target.value }))}
                  placeholder="Relevant medical history..."
                  rows={3}
                  style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              {/* Allergies */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>⚠️ Allergies</label>
                <textarea
                  className="form-control"
                  value={editForm.allergies}
                  onChange={(e) => setEditForm(prev => ({ ...prev, allergies: e.target.value }))}
                  placeholder="Known allergies..."
                  rows={2}
                  style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
            </div>

            <div className="modal-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditDialog(false)}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={savingEdit || !editForm.firstName?.trim()}
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address Dialog */}
      {showAddressDialog && (
        <div className="modal-overlay" onClick={() => setShowAddressDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px', width: '90%' }}>
            <h3>{(patient.street || patient.city || patient.state || patient.zipCode) ? '✏️ Edit Address' : '+ Add Address'}</h3>
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Street Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressForm.street}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, street: e.target.value }))}
                  placeholder="Enter street address"
                  style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>City</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="City"
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>State</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressForm.state}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="State"
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: '600', fontSize: '0.875rem' }}>Zip Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressForm.zipCode}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, zipCode: e.target.value }))}
                    placeholder="Zip"
                    style={{ width: '100%', padding: '0.5rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddressDialog(false)}
                disabled={savingAddress}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveAddress}
                disabled={savingAddress}
              >
                {savingAddress ? 'Saving...' : 'Save Address'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lobby Dialog */}
      {showLobbyDialog && (
        <div className="modal-overlay" onClick={() => setShowLobbyDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Patient to Waiting Area</h3>
            <p>
              <strong>Patient:</strong> {patient.firstName} {patient.lastName}
            </p>
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Visit Type:
              </label>
              <select
                className="form-control"
                value={lobbyVisitType}
                onChange={(e) => setLobbyVisitType(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem' }}
              >
                <option value="Visitation">Visitation</option>
                <option value="Treatment">Treatment</option>
              </select>
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowLobbyDialog(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddToLobby}
              >
                Add to Waiting Area
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientDetails;
